// A "review-only" finding has no safe automatic fix — the tool will not write to
// WordPress for it. Examples: an intentional `noindex` (thank-you / QA pages) or
// sitemap/indexability rows whose resolver explicitly returns action="review".
//
// Do not classify orphan_page / low_internal_links by issue type here. Those are
// recommendation-only for WordPress apply, but they still need a Generate fix
// path so operators can create/edit internal-link recommendations.

type ReviewOnlyInput = {
	issue_type?: string;
	proposed_value?: unknown;
	proposed_fix?: unknown;
	page_url?: string | null;
	wordpress_id?: number | null;
} | null | undefined;

// Internal-link recommendation types whose suggestions are user-selectable
// source lists. When such a payload actually CARRIES selectable sources, the
// finding must not be routed to the review-only dead end (no Apply button,
// "Request expert fix" as the only emphasized action) — the operator keeps
// autonomy over applying the automated recommendation.
const INTERNAL_LINK_TYPES = new Set(["orphan_page", "low_internal_links"]);

// Parse the structured suggestion payload. InternalLinkSuggestionCard reads
// `proposed_fix ?? proposed_value`; read the same pair here so the drawer's
// routing and the card's rendering always agree on which payload they saw.
function getSuggestionPayload(issue: ReviewOnlyInput): Record<string, unknown> | null {
	for (const raw of [issue?.proposed_fix, issue?.proposed_value]) {
		if (raw == null) continue;
		try {
			const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
			if (obj && typeof obj === "object") return obj as Record<string, unknown>;
		} catch {
			// not JSON — try the next source.
		}
	}
	return null;
}

// Review-only types that have a real WordPress write path the user can force
// (flip a page from noindex → index via the SEO plugin). Other review-only
// findings have no single API action and are manual-only (open in WordPress,
// fix by hand, re-check).
const OVERRIDABLE_REVIEW_TYPES = new Set([
	"indexability",
	"sitemap_error",
	// canonical: the cross-domain canonical gate returns review_only — the user
	// can "Apply anyway" to force a self-canonical write. Other canonical causes
	// (missing/mismatch/same_domain) are non-review and apply directly.
	"canonical",
]);

// proposed_value arrives as either a JSON string or an object; pull `.action`.
export function getProposedAction(issue: ReviewOnlyInput): string | null {
	const pv = issue?.proposed_value;
	if (pv == null) return null;
	try {
		const obj = typeof pv === "string" ? JSON.parse(pv) : pv;
		if (obj && typeof obj === "object" && "action" in obj) {
			const action = (obj as { action?: unknown }).action;
			return typeof action === "string" ? action : null;
		}
	} catch {
		// proposed_value wasn't JSON — not a structured review payload.
	}
	return null;
}

export function isReviewOnlyIssue(issue: ReviewOnlyInput): boolean {
	if (!issue) return false;
	if (getProposedAction(issue) !== "review") return false;
	// An internal-link "review" payload that still carries selectable sources is
	// NOT a dead end: keep the normal drawer (source checkboxes + Apply) so the
	// user can act on the automated options instead of being funneled to
	// "Request expert fix". Empty sources remain genuinely review-only.
	if (issue.issue_type && INTERNAL_LINK_TYPES.has(issue.issue_type)) {
		const payload = getSuggestionPayload(issue);
		const sources = payload?.recommended_source_pages;
		if (Array.isArray(sources) && sources.length > 0) return false;
	}
	return true;
}

// A review-only issue the user can choose to "Apply anyway" (force-override the
// safeguard). The backend still validates per cause — e.g. a canonicalised page
// has no automatic write and comes back `manual_required` — so this only gates
// whether the override button is offered, not whether the write will succeed.
export function isOverridableReviewIssue(issue: ReviewOnlyInput): boolean {
	if (!isReviewOnlyIssue(issue)) return false;
	return Boolean(issue?.issue_type && OVERRIDABLE_REVIEW_TYPES.has(issue.issue_type));
}

// Deep-link to the page's WordPress editor (post.php) so the user can fix a
// review-only finding by hand. Falls back to the live page URL if we don't have
// a WordPress post id.
export function getWordPressEditUrl(issue: ReviewOnlyInput): string | null {
	const pageUrl = issue?.page_url || null;
	if (issue?.wordpress_id && pageUrl) {
		try {
			const origin = new URL(pageUrl).origin;
			return `${origin}/wp-admin/post.php?post=${issue.wordpress_id}&action=edit`;
		} catch {
			// page_url wasn't a valid absolute URL — fall through.
		}
	}
	return pageUrl;
}
